import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { min, EmptyArray } from 'lib/array/arrayUtils'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useCroRoomAmenitiesInsteadOfDescription from 'hooks/OfferPage/useCroRoomAmenitiesInsteadOfDescription'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'
import ModalContext from 'contexts/ModalContext'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import WalledContent from 'components/Common/WalledContent'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import FlashHotelRoomDetailsModal from '../FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import AccommodationSameRoomTileImage from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileImage'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import { getAccommodationRoomTileActionsState } from 'components/AccommodationKit/getAccommodationRoomTileActionsState'
import FlashHotelRateOption from './FlashHotelRateOption'
import useToggle from 'hooks/useToggle'
import { AccommodationSameRoomTileOptions } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileOptions'
import useChildrenStayFree from 'hooks/Offers/useChildrenStayFree'
import { getDynamicPricingUrgencyLabelData } from 'components/OfferPage/utils'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import FlashHotelSoldOutDatesCarousel from '../FlashHotelSoldOutDatesCarousel'

interface Props {
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
  rooms: Array<OfferPageRoom>
  checkIn?: string
  availableRoomCount?: number
  availableForPurchase?: boolean
  previewMode: boolean
  canRedeemLuxPlusBenefits: boolean
  showSelection?: boolean
  headerRef?: React.RefObject<HTMLDivElement | null>
  suggestedPackageOption?: {
    option: App.OfferPackageOptionView
    message: string
  }
  ratesLayout: 'stacked' | 'carousel'
}

function isRenderablePackageView(packageView: App.OfferPackageOptionView, requiresRates: boolean) {
  return packageView.capacityValid && packageView.hotelAvailable && (!requiresRates || !!packageView.hasAvailableRates)
}

function FlashHotelSameRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    rooms,
    checkIn,
    availableRoomCount,
    availableForPurchase,
    canRedeemLuxPlusBenefits,
    showSelection = false,
    headerRef,
    suggestedPackageOption,
    ratesLayout,
    previewMode,
  } = props

  const intl = useIntl()
  const isMultiRoom = rooms.length > 1
  const roomCount = rooms.length

  const firstPackageOption = packageViews[0]?.package
  const pkg = isHotelPackage(firstPackageOption) ? firstPackageOption : undefined
  const roomType = pkg?.roomType

  const cachedRoomDateData = useUrgentOfferRoomDateData(offer.id, roomType?.id)
  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])

  const displayedRoomsBeforeSurcharge = useMemo(() => {
    if (!!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes') {
      return liveRoomsBeforeSurcharge
    }
    return undefined
  }, [liveRoomsBeforeSurcharge])

  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const contactContext = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const requiresRates = !!offer.property?.useDynamicCancellationPolicies && !!checkIn

  const carouselRef = useRef<HTMLDivElement | null>(null)

  const { value: inclusionsOpen, toggle: onInclusionsToggle } = useToggle()

  // Availability calculations
  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable) || previewMode,
  ], [packageViews, previewMode])

  const minAvailableRooms = min(packageViews, option => (option.availableRooms ?? 0))?.availableRooms
  const hasExceededCapacity = packageViews.every(option => option.hotelAvailable && !option.capacityValid)
  const renderablePackageViews = useMemo(
    () => packageViews.filter(option => isRenderablePackageView(option, requiresRates)),
    [packageViews, requiresRates],
  )
  const capacityValidPackageViews = useMemo(
    () => packageViews.filter(option => option.capacityValid),
    [packageViews],
  )
  const availablePackages = useMemo(
    () => renderablePackageViews.filter(option => (option.availableRooms ?? 0) >= roomCount),
    [renderablePackageViews, roomCount],
  )
  const isAllRoomsAvailable = packageViews.every(option => option.availableRooms && option.availableRooms >= roomCount)
  const isAllRoomsHaveAvailabilityData = packageViews.every(option => typeof option.availableRooms === 'number')
  // Use explicit === 0 check to distinguish "sold out" (0) from "loading" (undefined)
  const isSoldOut = !!checkIn && packageViews.every(option => option.availableRooms === 0)
  const { isAnyVariant: showSoldOutDatesCarousel } = useExperiment(Experiments.croSoldOutFlashPackagesState, {
    canActivate: isSoldOut,
  })
  const hasHotelMemberPrice = packageViews.some(option => option.hotelMemberPrice > 0)

  // For multi-room: check if selection is enabled
  const isSelectionEnabled = isMultiRoom
    ? (isAllRoomsAvailable && !isSoldOut && !hasExceededCapacity)
    : (datesAvailable && roomFitsCapacity)

  const callbacksFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const shouldPreventMultipleRoomsBooking = roomCount > 2 && canRedeemLuxPlusBenefits && hasHotelMemberPrice

  const canActivatePopularOptionBookingRemaining = !!availableRoomCount && !!checkIn
  const { isAnyVariant: isPopularOptionBookingRemainingVariant } = useExperiment(
    Experiments.croPopularOptionBookingRemaining,
    { canActivate: canActivatePopularOptionBookingRemaining },
  )
  const croPopularOptionBookingRemaining = canActivatePopularOptionBookingRemaining && isPopularOptionBookingRemainingVariant

  const includedGuestsByRate = useMemo(() => packageViews.map(view => view.package.roomRate?.includedGuests ?? []), [packageViews])

  const { freeStayLabel } = useChildrenStayFree({
    offerType: offer.type,
    filters: { rooms },
    includedGuestsByRate,
  })

  const cheapestPackageView = useMemo(
    () => packageViews.reduce((cheapest, current) => current.hotelPrice < cheapest.hotelPrice ? current : cheapest),
    [packageViews],
  )

  // Compute the state for AccommodationRoomTileActions
  // Don't show error messages when phone booking is required
  const actionsState = useMemo(() => {
    if (shouldPreventMultipleRoomsBooking) {
      return undefined
    }
    return getAccommodationRoomTileActionsState({
      availableForPurchase,
      roomFitsCapacity,
      datesAvailable,
      isMultiRoom,
      checkIn,
      isSoldOut,
      hasExceededCapacity,
      isAllRoomsAvailable,
      isAllRoomsHaveAvailabilityData,
    })
  }, [
    availableForPurchase,
    checkIn,
    datesAvailable,
    hasExceededCapacity,
    isAllRoomsAvailable,
    isAllRoomsHaveAvailabilityData,
    isMultiRoom,
    isSoldOut,
    roomFitsCapacity,
    shouldPreventMultipleRoomsBooking,
  ])

  // Selection tracking for multi-room
  const selectedPackageOptionViewIndex = useMemo(() => {
    if (!isMultiRoom) return 0
    return availablePackages.findIndex(packageView =>
      rooms.find(room => packageView.package.id === room.roomId && packageView.package.roomRate.id === room.rateId),
    ) + 1
  }, [availablePackages, rooms, isMultiRoom])

  // Auto-scroll to selection in multi-room mode
  useEffect(() => {
    if (!carouselRef.current || !isMultiRoom) return
    if (selectedPackageOptionViewIndex && showSelection) {
      const selectedElement = carouselRef.current.children[selectedPackageOptionViewIndex - 1]
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' })
      }
    }
  }, [selectedPackageOptionViewIndex, showSelection, isMultiRoom])

  // Event handlers
  const handleOpenCalendar = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, roomType])

  const onSelectIndividual = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
    headerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }, [offerPageDispatch, headerRef])

  const onShowRoomDetails = useCallback(() => {
    if (roomType) {
      showModal(<FlashHotelRoomDetailsModal room={roomType} />)
    }
  }, [roomType, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])
  const [roomDetailsInViewRef, roomDetailsInView] = useInView({ triggerOnce: true })

  let packagesToRender = renderablePackageViews
  if (previewMode) {
    packagesToRender = capacityValidPackageViews
  } else if (isMultiRoom) {
    packagesToRender = availablePackages
  }
  const carouselWidth = packagesToRender.length > 1 ? rem(300) : '100%'
  const hasHotelRoomAmenities = packagesToRender.some(view => (
    isHotelPackage(view.package) && view.package.roomType.amenities.length > 0
  ))
  const { isAnyVariant: shouldShowRoomAmenitiesInsteadOfDescription } = useCroRoomAmenitiesInsteadOfDescription({
    canActivate: hasHotelRoomAmenities && roomDetailsInView,
  })
  const showRoomAmenitiesInsteadOfDescription = hasHotelRoomAmenities && shouldShowRoomAmenitiesInsteadOfDescription

  if (!pkg || !roomType) {
    return null
  }

  const availabilityText = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)
    ? intl.formatMessage({
        id: 'xhccxef8IIv04RSBD16X',
        defaultMessage: '7 days a week from 9am to 7pm',
        description: 'Availability text for regions without 24/7 support',
      })
    : intl.formatMessage({
        id: 'URuGV_rVgQS0QR4q8ItC',
        defaultMessage: '24 hours, 7 days',
        description: 'Availability text for 24/7 support in the customer support section',
      })

  // Phone booking banner for special case
  const phoneBookingBanner = shouldPreventMultipleRoomsBooking ? (
    <Sheet background="light-background" padding={20}>
      <Group direction="vertical" gap={8}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '2pulspoB9cHo6Ojkjeaw',
              defaultMessage: 'Ready to book? Call us to complete your booking',
              description: 'Heading for phone booking banner in the booking or order summary view',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'UmTsldbaPmHF0U43v43X',
                defaultMessage: 'Our team are available {availability}',
                description: 'Informational text about customer support team availability and operating hours',
              },
              {
                availability: availabilityText,
              },
            )}
          </BodyText>
        </Group>
        <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${contactContext.defaultContact.number}`} fit="flex">
          {intl.formatMessage(
            {
              id: 'Npqv8j2BH95_AcfFxFlX',
              defaultMessage: 'Call {phoneNumber}',
              description: 'Action button label to call phone number on the offer detail page',
            },
            {
              phoneNumber: contactContext.defaultContact.humanReadable,
            },
          )}
        </TextButton>
        {callbacksFeatureEnabled && (
          <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
            {intl.formatMessage({
              id: 'w-5Xdyx85GaBmirZUAgh',
              defaultMessage: 'Request a callback',
              description: 'Action button label to request a callback on the offer detail page',
            })}
          </TextButton>
        )}
      </Group>
    </Sheet>
  ) : null

  const packageOptions = packagesToRender.map((view, index) => (
    <FlashHotelRateOption
      key={view.package.uniqueKey}
      packageView={view}
      roomsBeforeSurcharge={displayedRoomsBeforeSurcharge}
      packagePosition={packagesToRender.length > 1 ? index + 1 : undefined}
      inclusionsOpen={inclusionsOpen}
      onInclusionsToggle={onInclusionsToggle}
      suggestedPackageMessage={suggestedPackageOption?.option.package.uniqueKey === view.package.uniqueKey ? suggestedPackageOption.message : undefined}
    />
  ))

  return (
    <Pane data-testid="multi-same-room-room">
      <ScrollAnchor id={roomType.id} vOffset={rem(80)} />

      {/* Selection indicator for multi-room */}
      {isMultiRoom && !!selectedPackageOptionViewIndex && showSelection && (
        <Sheet background="highlight-primary" padding="4 20">
          <CaptionTextBlock variant="medium" startIcon={<LineArrowDownIcon />}>
            {intl.formatMessage({
              id: '9RnDXmXoFPP3Tj-YnCU9',
              defaultMessage: 'Your current selection',
              description: 'Label indicating the current room selection on the room page',
            })}
          </CaptionTextBlock>
        </Sheet>
      )}

      <AccommodationSameRoomTile roomPackage={pkg}>
        <AccommodationSameRoomTileImage roomPackage={pkg} />
        <AccommodationSameRoomTileDetails
          roomPackage={pkg}
          roomCount={roomCount}
          onShowRoomDetails={onShowRoomDetails}
          showHotelRoomAmenities={showRoomAmenitiesInsteadOfDescription}
          containerRef={roomDetailsInViewRef}
          urgencyLabels={<>
            {freeStayLabel && <StructureLabel variant="kids-free" startIcon={<LineKidIcon />}>{freeStayLabel}</StructureLabel>}
            {!datesAvailable && <UrgencyLabel variant="critical2">
              {intl.formatMessage({
                id: 'r939r00GL7ijU93v_IDp',
                defaultMessage: 'SOLD OUT',
                description: 'Label indicating sold out status on the offer detail page',
              })}
            </UrgencyLabel>}
            {checkIn && !croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && (
              <RoomsRemainingLabel roomCount={isMultiRoom ? (minAvailableRooms ?? 0) : (cheapestPackageView.availableRooms ?? 0)} threshold="rate" />
            )}
            {checkIn && croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && availableRoomCount && (
              <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} />
            )}
            {!!roomType.stats.booked && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && (
            <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={roomType.id} />
              </TripGuard>
            </CSSBreakpoint>
          )}
        >
          <AccommodationRoomTileActions
            roomPackage={pkg}
            state={actionsState}
            shouldShowAmenitiesAndPoliciesCta={!showRoomAmenitiesInsteadOfDescription}
            onChangeOccupancyClick={openCapacityFilter}
            onChangeDatesClick={handleOpenCalendar}
            onMultiRoomModeClick={onSelectIndividual}
          />
          {phoneBookingBanner}

        </AccommodationSameRoomTileDetails>
        <AccommodationSameRoomTileOptions>
          {isSoldOut && showSoldOutDatesCarousel && <FlashHotelSoldOutDatesCarousel roomType={roomType} />}
          {/* Package carousel */}
          {isSelectionEnabled && !shouldPreventMultipleRoomsBooking && packagesToRender.length > 0 && (
            <WalledContent
              enforced={offer.walledGarden}
              wall={(
                <Group fullHeight direction="vertical" verticalAlign="end">
                  <TextButton size="large" kind="secondary" onClick={onSignUpClick}>
                    {intl.formatMessage({
                      id: 'ifaKBmYHhtLQZ35Y4akM',
                      defaultMessage: 'Sign up to view exclusive rates',
                      description: 'Sign up button text to view exclusive rates on the offer detail page',
                    })}
                  </TextButton>
                </Group>
              )}
            >
              {ratesLayout === 'stacked' && packageOptions}
              {ratesLayout === 'carousel' && <CardCarousel
                snap="start"
                width={carouselWidth}
                gap={12}
                gutterSize={20}
                ref={carouselRef}
              >
                {packageOptions}
              </CardCarousel>}
            </WalledContent>
          )}
        </AccommodationSameRoomTileOptions>
      </AccommodationSameRoomTile>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'checkIn'
  | 'previewMode'
  | 'availableForPurchase'
  | 'canRedeemLuxPlusBenefits'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms ?? EmptyArray,
  checkIn: state.checkInDate,
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
}), FlashHotelSameRoomOption)
